import { createSelector } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import moment from 'moment';

import appSettings from 'wanderu-config';
import { googleDataSelectors } from 'wanderu-ducks-google';
import { searchResultsSelectors } from 'wanderu-ducks-psearch';
import { tripSelectors } from 'wanderu-ducks-trips';
import { createAppSelector } from 'wanderu-ducks-utils';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import {
    isSessionStorageAvailable,
} from 'wanderu-utils';

import NAMESPACE from './types';
import {
    selectUrlSearchParams,
    selectUrlFilterParams,
} from './urlSelectors';
import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
} from '../../constants';
import SLICE_NAMESPACE from '../../types';
import { buildPSearchParams } from '../../utils';

function _selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function _getOriginPlaceId(data) {
    return data.originPlaceId;
}

const _selectOriginPlaceId = createSelector(
    _selectData,
    _getOriginPlaceId,
);

function _selectQueryParams(data) {
    return data.queryParams;
}

const selectQueryParams = createSelector(
    _selectData,
    _selectQueryParams,
);

function _getPlaceText(place = {}) {
    return place.formatted_address;
}

const _selectOriginText = createSelector(
    _selectOriginPlace,
    _getPlaceText,
);

const _selectDestinationText = createSelector(
    _selectDestinationPlace,
    _getPlaceText,
);

function _selectOriginPlace(state) {
    const originPlaceId = _selectOriginPlaceId(state);
    return googleDataSelectors.selectPlaceDetails(state, originPlaceId);
}

function _getDestinationPlaceId(data) {
    return data.destinationPlaceId;
}

const _selectDestinationPlaceId = createSelector(
    _selectData,
    _getDestinationPlaceId,
);

function _selectDestinationPlace(state) {
    const destinationPlaceId = _selectDestinationPlaceId(state);
    return googleDataSelectors.selectPlaceDetails(state, destinationPlaceId);
}

function _selectCityName(place = {}) {
    return place.name;
}

const selectOriginCityName = createSelector(
    _selectOriginPlace,
    _selectCityName,
);

const selectDestinationCityName = createSelector(
    _selectDestinationPlace,
    _selectCityName,
);

function _getDirection(data) {
    return data.direction;
}

const _selectDirection = createSelector(
    _selectData,
    _getDirection,
);

function _getDepartDate(data) {
    return data.departDate;
}

const _selectDepartDate = createSelector(
    _selectData,
    _getDepartDate,
);

function _getReturnDate(data) {
    return data.returnDate;
}

const _selectReturnDate = createSelector(
    _selectData,
    _getReturnDate,
);

function _getDepartTripId(data) {
    return data.departTripId;
}

const selectDepartTripId = createSelector(
    _selectData,
    _getDepartTripId,
);

function selectDepartTrip(state) {
    const departTripId = selectDepartTripId(state);
    return tripSelectors.selectTrip(state, departTripId);
}

const _selectReduxSearchParams = createAppSelector(
    [
        (_, direction) => direction,
        _selectOriginPlaceId,
        _selectOriginText,
        _selectDestinationPlaceId,
        _selectDestinationText,
        _selectDepartDate,
        _selectReturnDate,
        selectDepartTripId,
    ],
    (...args) => ({
        departDate: args[5],
        departTripId: args[7],
        destinationPlaceId: args[3],
        destinationText: args[4],
        direction: args[0],
        originPlaceId: args[1],
        originText: args[2],
        returnDate: args[6],
    }),
);

function selectReduxSearchParams(state, direction) {
    const resolvedDirection = direction || _selectDirection(state);
    return _selectReduxSearchParams(state, resolvedDirection);
}

function _combineSearchParams(reduxParams, urlParams) {
    return {
        departDate: reduxParams.departDate || urlParams.departDate,
        departTripId: reduxParams.departTripId || urlParams.departTripId,
        destinationPlaceId: reduxParams.destinationPlaceId || urlParams.destinationPlaceId,
        destinationText: reduxParams.destinationText || urlParams.destinationText,
        direction: reduxParams.direction || urlParams.direction,
        originPlaceId: reduxParams.originPlaceId || urlParams.originPlaceId,
        originText: reduxParams.originText || urlParams.originText,
        returnDate: reduxParams.returnDate || urlParams.returnDate,
    };
}

const _selectSearchParams = createAppSelector(
    [
        (state) => selectReduxSearchParams(state),
        (state, urlProps) => selectUrlSearchParams(urlProps),
    ],
    _combineSearchParams,
);

function selectSearchParams(state, ownProps) {
    const urlProps = {
        location: ownProps.location,
        match: ownProps.match,
    };

    return _selectSearchParams(state, urlProps);
}

function _getReturnTripId(data) {
    return data.returnTripId;
}

const selectReturnTripId = createSelector(
    _selectData,
    _getReturnTripId,
);

function selectServerBatchId(state) {
    const data = _selectData(state);
    return data.serverBatchId;
}

function _buildPSearchParams(
    direction,
    originPlace,
    destinationPlace,
    departDate,
    returnDate,
    locale,
    currency,
    seoReferrer,
) {
    if (!direction || !originPlace || !destinationPlace || !departDate) {
        return undefined;
    }

    if (direction === RETURN_DIRECTION && !returnDate) {
        return undefined;
    }

    return buildPSearchParams(direction, {
        currency,
        departDate,
        destinationPlace,
        locale,
        originPlace,
        returnDate,
        seoReferrer,
    });
}

function _selectSeoRefferer() {
    if (!appSettings.env.isClient) {
        return undefined;
    }

    if (!isSessionStorageAvailable()) {
        return undefined;
    }

    return window.sessionStorage.getItem('seo_referrer') || null;
}

const _selectPSearchParams = createAppSelector(
    [
        (_, direction) => direction,
        _selectOriginPlace,
        _selectDestinationPlace,
        _selectDepartDate,
        _selectReturnDate,
        selectLocale,
        selectCurrency,
        _selectSeoRefferer,
    ],
    _buildPSearchParams,
);

const selectSearchParamsAreValid = createAppSelector(
    [
        (state) => selectReduxSearchParams(state),
        (state, urlProps) => selectUrlSearchParams(urlProps),
    ],
    isEqual,
);

function selectPSearchParams(state, searchParams) {
    const resolvedParams = searchParams || selectReduxSearchParams(state);
    return _selectPSearchParams(state, resolvedParams.direction);
}

const CONNECTING_TRIP_TIME_WINDOW = 60 * 60 * 1000;
function _computeLeavesAfterDate(direction, departTrip) {
    if (!departTrip || direction === DEPART_DIRECTION) {
        return undefined;
    }

    return moment.parseZone(departTrip.localizedArriveDateTime)
        .add(CONNECTING_TRIP_TIME_WINDOW)
        .format();
}

const selectLeavesAfter = createAppSelector(
    [
        (state, searchParams) => searchParams
            ? searchParams.direction
            : _selectDirection(state),
        selectDepartTrip,
    ],
    _computeLeavesAfterDate,
);

function selectUserInputForPlaceId(state, placeId) {
    const data = _selectData(state);
    return data.inputs[placeId];
}

function selectOriginUserInput(state) {
    const data = _selectData(state);
    return data.inputs.current.origin;
}

function selectDestinationUserInput(state) {
    const data = _selectData(state);
    return data.inputs.current.destination;
}

function selectOriginUserPlaceId(state) {
    const data = _selectData(state);
    return data.inputs.current.originPlaceId;
}

function selectDestinationUserPlaceId(state) {
    const data = _selectData(state);
    return data.inputs.current.destinationPlaceId;
}

function selectBatchId(state, searchParams) {
    const psearchParams = selectPSearchParams(state, searchParams);
    return searchResultsSelectors.selectBatchId(state, psearchParams);
}

export {
    selectBatchId,
    selectDepartTripId,
    selectDestinationCityName,
    selectDestinationUserInput,
    selectDestinationUserPlaceId,
    selectLeavesAfter,
    selectOriginCityName,
    selectOriginUserInput,
    selectOriginUserPlaceId,
    selectPSearchParams,
    selectQueryParams,
    selectReduxSearchParams,
    selectReturnTripId,
    selectSearchParams,
    selectSearchParamsAreValid,
    selectServerBatchId,
    selectUrlFilterParams,
    selectUrlSearchParams,
    selectUserInputForPlaceId,
};
